// 📈 실시간 매출 조회 (기공소) — 2026-07-26 사장 기획
// 명세서를 만들지 않아도, 기간 내 의뢰서(웹+에이전트 수집)에 치과별 저장 단가표를 적용해
// 치과별 예상 매출과 총합을 즉시 계산한다. (단가표가 저장된 치과에 한해 금액 산출)
import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, where, getDocs, doc, onSnapshot } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { db, auth } from '../firebase/config';
import { TrendingUp, RefreshCw, AlertCircle } from 'lucide-react';
import PageHeader from './common/PageHeader';
import PriceListModal from './PriceListModal';
import { buildItemsFromOrders, normalizePriceItems, getTimestamp } from '../utils/statementItems';
// 💰 [품목 보기] (2026-10-07 기획 §3-4 · 법무 ⑤) — 치과가 보는 「예상 기공료」 화면을 기공소가 같은 모달로 확인. 서버 호출 0(웹 정본 엔진 결과 재사용)
import ExpectedStatementModal from './ExpectedStatementModal';
import { EXPSTMT_DISCLAIMER, EXPSTMT_UNATTRIBUTED_LABEL, EXPSTMT_MAX_ROWS, inclusiveDays } from '../services/expectedStatementApi';
// 케이스 통합 뷰 — 1단계 섀도 비교 전용(화면 출력 불변). docs/기획서_케이스중심통일_2026-08.md
import { mergeCases, ordersOf, diffItems, logShadowDiff, normClinicKey } from '../utils/caseView';
import { loadCasesForLab } from '../utils/caseViewLoad';
import { getFeatures, isCaseUnifyRead, isCaseUnifyDisplay } from '../utils/features';

const fmt = (n) => (Math.round(n) || 0).toLocaleString('ko-KR');
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const today = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const norm = (s) => (s || '').replace(/\s|\(.*?\)/g, '').replace(/치과의원|치과병원|치과$/g, '').toLowerCase();

// ── 케이스 통일 섀도 비교 (1단계, 2026-08-09) ──────────────────────────────
// 현행(A: agent_auto 주문 기반)과 신규 통합 뷰(B: parsedCases 직접)를 치과별로 대조해 차이만 로깅한다.
// 플래그 config/features.caseUnify.read=true일 때만 동작하며, 화면 표시는 절대 건드리지 않는다.
async function runCaseUnifyShadow({ uid, from, to, groups, priceByName, currentItemsByClinic }) {
    try {
        const features = await getFeatures();
        if (!isCaseUnifyRead(features)) return;
        const cases = await loadCasesForLab({ labId: uid, from, to });
        // 수집 케이스를 현행 치과 그룹명에 배정 (이름 정규화 키). 매칭 없으면 파일의 의뢰처명으로 새 그룹
        const groupByKey = new Map([...groups.keys()].map(name => [normClinicKey(name), name]));
        const casesByClinic = new Map();
        for (const c of cases) {
            const raw = (c.partnerName || c.clientNameInFile || '미상').trim();
            const name = groupByKey.get(normClinicKey(raw)) || raw;
            if (!casesByClinic.has(name)) casesByClinic.set(name, []);
            casesByClinic.get(name).push(c);
        }
        const clinicNames = new Set([...groups.keys(), ...casesByClinic.keys()]);
        let totalCur = 0, totalNext = 0, diffClinics = 0;
        for (const clinic of clinicNames) {
            const raw = groups.get(clinic) || [];
            const priceList = priceByName.get(normClinicKey(clinic)) || [];
            const rows = mergeCases({
                webOrders: raw.filter(o => o.source !== 'agent_auto'),
                legacyAgentOrders: raw.filter(o => o.source === 'agent_auto'),
                cases: casesByClinic.get(clinic) || [],
                basis: 'createdAt', partnerName: clinic, labId: uid,
            });
            const nextItems = buildItemsFromOrders(ordersOf(rows), { priceList });
            const d = diffItems(currentItemsByClinic.get(clinic) || [], nextItems);
            totalCur += d.amountCurrent; totalNext += d.amountNext;
            if (!d.ok) { diffClinics++; logShadowDiff(`예상 매출 · ${clinic} · ${from}~${to}`, d, { 통합행: rows.length }); }
        }
        console.log(`[케이스통일 섀도] 예상 매출 합계 대조 ${from}~${to} — 현행 ${Math.round(totalCur).toLocaleString('ko-KR')}원 / 신규 ${Math.round(totalNext).toLocaleString('ko-KR')}원 · 차이 나는 치과 ${diffClinics}곳`);
    } catch (e) {
        console.warn('[케이스통일 섀도] 예상 매출 비교 실패:', e);
    }
}

function RevenueQuickView({ user }) {
    const uid = user?.companyId || auth.currentUser?.uid;
    const [from, setFrom] = useState(monthStart());
    const [to, setTo] = useState(today());
    const [rows, setRows] = useState(null); // null=미조회
    const [loading, setLoading] = useState(false);
    const [total, setTotal] = useState(0);
    const [priceClinic, setPriceClinic] = useState(null);   // {id, name} — 단가표 관리 창으로 진입
    const [itemsView, setItemsView] = useState(null);       // [품목 보기] 모달 데이터 (계약 §1 모양) + _clinicId·_clinic
    const [confirmedWarn, setConfirmedWarn] = useState('');  // 확정 명세서 대조 실패 안내(조용한 실패 금지)
    const navigate = useNavigate();

    // 💰 치과에 예상 기공비 표시 여부 배지 (2026-10-07) — labShareSettings/{labUid}.expectedCost[clinicUid].enabled=false 면 '표시 안 함'.
    //   rules 상 기공소 대표 본인만 읽을 수 있어 직원·읽기 실패 시 배지를 생략한다(모르는 것을 표시하지 않는다). 토글은 설정 페이지에서만.
    const [shareDoc, setShareDoc] = useState(undefined);   // undefined=모름(배지 생략), {}=문서 없음(전부 켜짐)
    useEffect(() => {
        const me = auth.currentUser?.uid;
        if (!me || user?.companyId || user?.userType === 'staff') { setShareDoc(undefined); return; }
        const unsub = onSnapshot(doc(db, 'labShareSettings', me),
            (snap) => setShareDoc(snap.exists() ? snap.data() : {}),
            (e) => { console.warn('[예상 매출] 치과 표시 설정 읽기 실패(배지 생략):', e?.message); setShareDoc(undefined); });
        return () => unsub();
    }, [user?.companyId, user?.userType]);
    const costShareBadge = (clinicId) => {
        if (shareDoc === undefined || !clinicId) return null;   // 미가입 치과(agent_/email_ 키)는 볼 계정이 없음 — 배지 없음
        const off = shareDoc?.expectedCost?.[clinicId]?.enabled === false;
        return (
            <button onClick={() => navigate('/settings/clinic-progress')}
                title="치과 「기공비 분석」에 예상 기공비를 표시할지 — 설정 › 치과에 공정 공개에서 바꿉니다"
                style={{ marginLeft: 6, background: off ? '#f1f5f9' : '#EDFBF1', border: `1px solid ${off ? '#e2e8f0' : '#BBF7D0'}`, color: off ? '#64748b' : '#15803D', borderRadius: 6, padding: '1px 6px', fontSize: 10.5, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                {off ? '치과에 표시 안 함' : '치과에 표시 중'}
            </button>
        );
    };

    const run = useCallback(async () => {
        if (!uid) return;
        setLoading(true);
        try {
            const start = new Date(`${from}T00:00:00`);
            const end = new Date(`${to}T23:59:59`);

            // 1) 기간 내 수신 의뢰서 전부 (웹 + 에이전트 수집)
            const snap = await getDocs(query(collection(db, 'workOrders'), where('toUserId', '==', uid)));
            const orders = snap.docs.map(d => ({ id: d.id, ...d.data() })).filter(o => {
                const t = o.createdAt?.toDate ? o.createdAt.toDate() : new Date(o.createdAt);
                return t && t >= start && t <= end;
            });

            // 1.5) 연결된 회원 치과 (이름→id) — 기간 내 의뢰서에 fromUserId가 없어도 단가표 진입 가능하게
            const memberByNorm = new Map();
            try {
                const { doc: docRef, getDoc } = await import('firebase/firestore');
                const [cs1, cs2] = await Promise.all([
                    getDocs(query(collection(db, 'connections'), where('requesterId', '==', uid), where('status', '==', 'accepted'))),
                    getDocs(query(collection(db, 'connections'), where('receiverId', '==', uid), where('status', '==', 'accepted'))),
                ]);
                const pids = new Set();
                [...cs1.docs, ...cs2.docs].forEach(d => {
                    const c = d.data();
                    const pid = c.requesterId === uid ? c.receiverId : c.requesterId;
                    if (pid) pids.add(pid);
                });
                for (const pid of pids) {
                    try {
                        const u = await getDoc(docRef(db, 'users', pid));
                        if (u.exists()) memberByNorm.set(norm(u.data().businessName || u.data().name || ''), pid);
                    } catch (_) { /* 개별 실패 무시 */ }
                }
            } catch (e) { console.warn('연결 치과 조회 실패:', e); }

            // 2) 저장된 단가표 전부 (치과명 매칭용)
            const plSnap = await getDocs(query(collection(db, 'priceLists'), where('userId', '==', uid)));
            const priceByName = new Map();
            plSnap.docs.forEach(d => {
                const x = d.data();
                if ((x.items || []).length) priceByName.set(norm(x.partnerName || String(d.id).split('_').slice(1).join('_')), normalizePriceItems(x.items));
            });

            // 2.5) [품목 보기]용 확정 명세서 색인 (2026-10-07) — 서버 revenue-engine.indexConfirmedItems 와 같은 키(items[].orderId / parsedCaseId).
            //   내가 발행한 확정본만(where 필수). 실패해도 매출 계산은 계속하되 화면에 1줄 알린다.
            const confirmedIdx = { byOrderId: new Map(), byCaseId: new Map(), numberById: new Map() };
            try {
                const stSnap = await getDocs(query(collection(db, 'transactionStatements'), where('fromUserId', '==', uid), where('status', '==', 'confirmed')));
                const stripCase = (v) => (v == null ? '' : String(v)).replace(/^case:/, '');
                const put = (m, k, v) => { if (!k) return; if (!m.has(k)) m.set(k, []); m.get(k).push(v); };
                stSnap.docs.forEach(d => {
                    const st = d.data();
                    if (st.deletedBySender) return;
                    confirmedIdx.numberById.set(d.id, st.statementNumber || '');
                    (st.items || []).forEach(it => {
                        const amount = Number(it.totalPrice ?? it.price ?? ((it.toothCount || 0) * (it.unitPrice || 0))) || 0;
                        const v = { stmtId: d.id, amount };
                        if (it.orderId) {
                            put(confirmedIdx.byOrderId, String(it.orderId), v);
                            if (stripCase(it.orderId) !== String(it.orderId)) put(confirmedIdx.byCaseId, stripCase(it.orderId), v);
                        }
                        if (it.parsedCaseId) put(confirmedIdx.byCaseId, stripCase(it.parsedCaseId), v);
                    });
                });
                setConfirmedWarn('');
            } catch (e) {
                console.error('[예상 매출] 확정 명세서 대조 조회 실패 — [품목 보기]에 "명세서 포함" 표시가 빠집니다:', e);
                setConfirmedWarn('확정 명세서 대조에 실패해 [품목 보기]의 "명세서 포함" 표시가 빠질 수 있습니다.');
            }

            // 3) 치과별 그룹 → 단가표 적용 합산 (명세서 작성과 동일 로직 buildItemsFromOrders)
            const groups = new Map();
            orders.forEach(o => {
                const name = o.fromUserName || o.clientName || '미상';
                if (!groups.has(name)) groups.set(name, []);
                groups.get(name).push(o);
            });

            // 중복 제거 (2026-07-28): 명세서 작성과 동일 원칙 — 같은 환자·치식이면 웹 우선, 수집분끼리도 1건만
            const teethOf = (o) => {
                const t = [];
                (o.items || []).forEach(it => { if (it.toothNumber) t.push(String(it.toothNumber)); });
                (o.prosthesisGroups || []).forEach(g => (g.teeth || []).forEach(x => t.push(String(x))));
                (o.bridgeGroups || []).forEach(g => (g.teeth || []).forEach(x => t.push(String(x))));
                return [...new Set(t)].sort().join(',');
            };
            const nn = (x) => (x || '').replace(/[\s\d\-_.()[\]]/g, '').toLowerCase();
            const result = [];
            const currentItemsByClinic = new Map();   // 섀도 비교용 (화면 미사용)
            let sum = 0;

            // ── 케이스 통일 표시 전환 (1단계 마무리, 2026-08-17 대표 확정 "신규가 옳다") ──
            // 플래그 ON이면 치과별 목록을 통합 뷰(B: parsedCases 최신 파싱)로 만든다. OFF면 현행(A) 그대로.
            let unifiedDisplay = false;
            try { unifiedDisplay = isCaseUnifyDisplay(await getFeatures()); } catch (_) { unifiedDisplay = false; }

            if (unifiedDisplay) {
                const cases = await loadCasesForLab({ labId: uid, from, to });
                const groupByKey = new Map([...groups.keys()].map(name => [normClinicKey(name), name]));
                const casesByClinic = new Map();
                for (const c of cases) {
                    const raw = (c.partnerName || c.clientNameInFile || '미상').trim();
                    const name = groupByKey.get(normClinicKey(raw)) || raw;
                    if (!casesByClinic.has(name)) casesByClinic.set(name, []);
                    casesByClinic.get(name).push(c);
                }
                const clinicNames = new Set([...groups.keys(), ...casesByClinic.keys()]);
                for (const clinic of clinicNames) {
                    const rawList = groups.get(clinic) || [];
                    const priceList = priceByName.get(norm(clinic)) || [];
                    const mergedRows = mergeCases({
                        webOrders: rawList.filter(o => o.source !== 'agent_auto'),
                        legacyAgentOrders: rawList.filter(o => o.source === 'agent_auto'),
                        cases: casesByClinic.get(clinic) || [],
                        basis: 'createdAt', partnerName: clinic, labId: uid,
                    });
                    const list = ordersOf(mergedRows);
                    const items = buildItemsFromOrders(list, { priceList });
                    const amount = items.reduce((a, it) => a + (Number(it.toothCount) || 1) * (Number(it.unitPrice) || 0), 0);
                    const unpriced = items.filter(it => !Number(it.unitPrice)).length;
                    sum += amount;
                    const clinicId = (rawList.find(o => o.fromUserId) || {}).fromUserId
                        || ((casesByClinic.get(clinic) || []).find(c => c.partnerId) || {}).partnerId
                        || memberByNorm.get(norm(clinic)) || null;
                    result.push({ clinic, clinicId, orders: list.length, items: items.length, amount, unpriced, hasPrice: priceList.length > 0, _items: items, _orders: list, _confirmedIdx: confirmedIdx });
                }
                console.log(`[케이스통일] 예상 매출 표시=통합 뷰(B) · 치과 ${result.length}곳`);
            } else {
                for (const [clinic, rawList] of groups.entries()) {
                    const web = rawList.filter(o => o.source !== 'agent_auto');
                    const seen = web.map(o => nn(o.patientName) + '|' + teethOf(o));
                    const list = [...web];
                    for (const ao of rawList.filter(o => o.source === 'agent_auto')) {
                        const k = nn(ao.patientName) + '|' + teethOf(ao);
                        if (!ao.isRemake && seen.includes(k)) continue;
                        seen.push(k);
                        list.push(ao);
                    }
                    const priceList = priceByName.get(norm(clinic)) || [];
                    const items = buildItemsFromOrders(list, { priceList });
                    currentItemsByClinic.set(clinic, items);
                    // buildItemsFromOrders는 amount를 만들지 않음 — 치아수×단가로 계산 (2026-07-26 실사 수정)
                    const amount = items.reduce((a, it) => a + (Number(it.toothCount) || 1) * (Number(it.unitPrice) || 0), 0);
                    const unpriced = items.filter(it => !Number(it.unitPrice)).length;
                    sum += amount;
                    // 회원 치과면 단가표 관리 진입용 id 확보 — 의뢰서 발신자 id 우선, 없으면 연결 목록에서 이름 매칭
                    const clinicId = (rawList.find(o => o.fromUserId) || {}).fromUserId || memberByNorm.get(norm(clinic)) || null;
                    result.push({ clinic, clinicId, orders: list.length, items: items.length, amount, unpriced, hasPrice: priceList.length > 0, _items: items, _orders: list, _confirmedIdx: confirmedIdx });
                }
            }
            result.sort((a, b) => b.amount - a.amount);
            setRows(result);
            setTotal(sum);

            // ── 케이스 통일 섀도 비교 (1단계) — 표시가 A일 때만 A/B 차이를 로깅 (표시가 B면 생략)
            if (!unifiedDisplay) runCaseUnifyShadow({ uid, from, to, groups, priceByName, currentItemsByClinic });
        } catch (e) {
            console.error('매출 조회 실패:', e);
            alert('매출 조회에 실패했습니다.');
        }
        setLoading(false);
    }, [uid, from, to]);

    useEffect(() => { if (uid) run(); /* eslint-disable-next-line */ }, [uid]); // uid 준비 후 자동 조회 (직원 계정 0원 버그 수정)

    // ── [품목 보기] — 치과 행의 웹 정본 엔진 items 를 계약 §1 응답 모양으로 바꿔 같은 모달에 띄운다 (서버 호출 0, 쓰기 0) ──
    //   표시 규칙은 서버 functions/lib/expected-statement.js buildStatementRows 와 동일하게:
    //   · 귀속 미확인 = 항목의 원 주문/케이스 소유 uid(fromUserId=partnerId)가 이 치과 uid 와 다르면 환자명을 "자동 수집 건(거래처 확인 전)"으로 대체(금액은 유지)
    //     — 회원 치과(clinicId 있음)만 검사. 미가입 치과(clinicId 없음)는 볼 치과 계정이 없으므로 기공소 자기 데이터 그대로(법무 ⑤: 기공소가 더 보는 것은 무방)
    //   · 리메이크 단가 0 "확인 필요" · 미등록 "미등록" · 확정 명세서 포함 배지(items[].orderId/parsedCaseId 색인) · 500행 상한 truncated
    const openItems = (r) => {
        const idx = r._confirmedIdx || { byOrderId: new Map(), byCaseId: new Map(), numberById: new Map() };
        const stripCase = (v) => (v == null ? '' : String(v)).replace(/^case:/, '');
        const orderById = new Map((r._orders || []).map(o => [String(o.id), o]));
        const seenHit = new Set();
        const confirmedOrders = new Set();
        let confirmedAmount = 0;
        let unattributedRows = 0, unpricedCount = 0, remakeCount = 0, teeth = 0, rawAmount = 0;
        const rows = (r._items || []).map((it, i) => {
            const toothCount = Number(it.toothCount) || 1;
            const unitPrice = Number(it.unitPrice) || 0;
            const amount = toothCount * unitPrice;
            rawAmount += amount;
            teeth += Number(it.toothCount) || 0;
            if (!unitPrice) unpricedCount++;
            if (it.isRemake) remakeCount++;
            const oid = String(it.orderId == null ? '' : it.orderId);
            const o = orderById.get(oid);
            const owner = o ? (o.fromUserId || null) : null;
            const unattributed = !!r.clinicId && (!owner || owner !== r.clinicId);
            if (unattributed) unattributedRows++;
            // 확정 명세서 대조 — orderId · case: 접두 제거 id · parsedCaseId 세 키
            const hits = [
                ...(idx.byOrderId.get(oid) || []),
                ...(stripCase(oid) !== oid ? (idx.byCaseId.get(stripCase(oid)) || []) : []),
                ...(it.parsedCaseId ? (idx.byCaseId.get(stripCase(it.parsedCaseId)) || []) : []),
            ];
            let confirmed = null;
            if (hits.length) {
                const ids = [...new Set(hits.map(h => h.stmtId))].sort();
                const withNo = ids.map(id => idx.numberById.get(id) || '').filter(Boolean);
                confirmed = { statementNumber: withNo[0] || '', count: ids.length };
                confirmedOrders.add(oid);
                hits.forEach(h => { if (!seenHit.has(h)) { seenHit.add(h); confirmedAmount += h.amount; } });
            }
            const toothInfo = String(it.toothInfo || '');
            const sp = toothInfo.indexOf(' ');
            const itemLabel = sp > 0 ? toothInfo.slice(sp + 1).trim() : (toothInfo.startsWith('#') ? '' : (toothInfo === '-' ? '' : toothInfo));
            return {
                key: `r${i + 1}`,
                source: it.source === 'auto' ? 'auto' : 'order',
                createdAtMs: getTimestamp(it.createdAt) || 0,
                dueDateMs: getTimestamp(it.dueDate) || null,
                patientName: unattributed ? EXPSTMT_UNATTRIBUTED_LABEL : String(it.patientName || ''),
                toothInfo, toothCount, itemLabel,
                material: String(it.material || ''),
                unitPrice, amount,
                priced: unitPrice > 0,
                isRemake: !!it.isRemake,
                remakeKind: it.isRemake ? String(it.remakeKind || '') : '',
                confirmed, unattributed,
                orderId: (!unattributed && it.source !== 'auto' && oid && !oid.startsWith('case:')) ? oid : null,
            };
        }).sort((a, b) => (a.createdAtMs - b.createdAtMs));
        const truncated = rows.length > EXPSTMT_MAX_ROWS;
        setItemsView({
            shared: true,
            labName: user?.businessName || user?.companyName || user?.name || '우리 기공소',
            clinicName: r.clinic,
            range: { from, to, days: inclusiveDays(from, to) },
            basis: 'current_price',
            rows: truncated ? rows.slice(0, EXPSTMT_MAX_ROWS) : rows,
            summary: {
                amount: Math.round(rawAmount), itemCount: rows.length, teeth,
                unpricedCount, remakeCount,
                confirmedCount: confirmedOrders.size, confirmedAmount: Math.round(confirmedAmount),
                hasPrice: !!r.hasPrice,
                unmatchedByName: { count: 0 },   // 기공소 화면은 이름 폴백 행도 치과 행으로 이미 묶여 있음 — 제외 건수 없음
                unattributedPatientRows: unattributedRows,
                truncated,
            },
            disclaimer: EXPSTMT_DISCLAIMER,   // ☆ 서버 상수 사본(expectedStatementApi.js) — 서버와 글자 동일 유지
            _clinicId: r.clinicId,
            _clinic: r.clinic,
        });
    };

    return (
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '24px 16px' }}>
            <PageHeader icon={TrendingUp} title="예상 매출 조회" />
            <p style={{ color: '#64748b', fontSize: 13.5, margin: '4px 0 16px', lineHeight: 1.7 }}>
                기간 내 의뢰서(웹 + 에이전트 수집)에 <b>저장된 치과별 단가표</b>를 적용해 예상 매출을 즉시 계산합니다.
                명세서 발행 여부와 무관한 실시간 추정치이며, 단가표가 없는 치과는 0원으로 표시되며, 미가입 치과도 단가표 칸을 눌러 등록할 수 있습니다.
            </p>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', marginBottom: 18 }}>
                <input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputS} />
                <span style={{ color: '#94a3b8' }}>~</span>
                <input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputS} />
                <button onClick={run} disabled={loading} style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 18px', border: 'none',
                    borderRadius: 8, background: '#6366f1', color: '#fff', fontWeight: 700, fontSize: 13.5, cursor: 'pointer',
                }}>
                    <RefreshCw size={14} /> {loading ? '계산 중...' : '조회'}
                </button>
            </div>

            {/* 총합 카드 */}
            <div style={{ background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 14, padding: '18px 22px', marginBottom: 18, display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 10 }}>
                <span style={{ fontSize: 13.5, fontWeight: 700, color: '#4338ca' }}>기간 예상 매출 합계</span>
                <span style={{ fontSize: 26, fontWeight: 800, color: '#312e81', fontVariantNumeric: 'tabular-nums' }}>{fmt(total)}원</span>
                <span style={{ fontSize: 12, color: '#6366f1' }}>{from} ~ {to} · 치과 {rows ? rows.length : 0}곳</span>
            </div>

            {rows === null ? null : rows.length === 0 ? (
                <div style={{ textAlign: 'center', padding: 50, color: '#9ca3af', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 }}>기간 내 의뢰서가 없습니다.</div>
            ) : (
                <div style={{ border: '1px solid #e2e8f0', borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5 }}>
                        <thead>
                            <tr style={{ background: '#f8fafc', color: '#64748b' }}>
                                <th style={thS}>치과</th>
                                <th style={{ ...thS, textAlign: 'center' }}>의뢰서</th>
                                <th style={{ ...thS, textAlign: 'center' }}>품목</th>
                                <th style={{ ...thS, textAlign: 'right' }}>예상 매출</th>
                                <th style={{ ...thS, textAlign: 'center' }}>단가표</th>
                                <th style={{ ...thS, textAlign: 'center' }}>품목</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map(r => (
                                <tr key={r.clinic} style={{ borderTop: '1px solid #f1f5f9' }}>
                                    <td style={tdS}>{r.clinic}{costShareBadge(r.clinicId)}</td>
                                    <td style={{ ...tdS, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{r.orders}건</td>
                                    <td style={{ ...tdS, textAlign: 'center', fontVariantNumeric: 'tabular-nums' }}>{r.items}개</td>
                                    <td style={{ ...tdS, textAlign: 'right', fontWeight: 700, fontVariantNumeric: 'tabular-nums' }}>{fmt(r.amount)}원</td>
                                    <td style={{ ...tdS, textAlign: 'center' }}>
                                        {(() => {
                                            const label = !r.hasPrice
                                                ? <span style={{ fontSize: 11.5, color: '#dc2626', display: 'inline-flex', alignItems: 'center', gap: 3 }}><AlertCircle size={12} /> 미등록</span>
                                                : r.unpriced > 0
                                                    ? <span style={{ fontSize: 11.5, color: '#b45309' }}>단가 없는 품목 {r.unpriced}개</span>
                                                    : <span style={{ fontSize: 11.5, color: '#059669' }}>적용됨</span>;
                                            // 클릭 → 단가표 관리 창 (저장 후 자동 재계산). 미가입은 이름 키로 저장 (2026-07-30 B안)
                                            return (
                                                <button onClick={() => setPriceClinic({ id: r.clinicId || `agent_${r.clinic}`, name: r.clinic })}
                                                    title="단가표 관리 창을 엽니다 — 저장하면 매출이 다시 계산됩니다"
                                                    style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline', textUnderlineOffset: 3 }}>
                                                    {label}
                                                </button>
                                            );
                                        })()}
                                    </td>
                                    <td style={{ ...tdS, textAlign: 'center' }}>
                                        {/* 치과가 「받은 명세서」 기간 예상 기공료에서 보는 것과 같은 화면 (2026-10-07) */}
                                        <button onClick={() => openItems(r)}
                                            title="이 치과가 덴클에서 보는 「예상 기공료」 화면과 같은 내용을 품목별로 봅니다"
                                            style={{ background: '#fff', border: '1px solid #e2e8f0', color: '#334155', borderRadius: 6, padding: '4px 10px', fontSize: 12, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                                            품목 보기
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 14, lineHeight: 1.7 }}>
                ⓘ 단가표 칸을 누르면 이 자리에서 바로 등록·수정할 수 있습니다.
                단가표를 채울수록 이 화면의 매출이 정확해집니다.
            </p>
            {confirmedWarn && (
                <p style={{ fontSize: 12, color: '#b45309', marginTop: 6, lineHeight: 1.7 }}>{confirmedWarn}</p>
            )}
            {priceClinic && (
                <PriceListModal user={user} clinicId={priceClinic.id} clinicName={priceClinic.name}
                    onClose={() => setPriceClinic(null)} onSaved={() => run()} />
            )}
            {/* 💰 [품목 보기] 모달 — 치과 화면과 같은 ExpectedStatementModal. 기공소 전용 하단: 표시 중/안 함 배지 + [단가표 수정] */}
            {itemsView && (
                <ExpectedStatementModal
                    data={itemsView}
                    viewer="lab"
                    footerExtra={(
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                            {costShareBadge(itemsView._clinicId)}
                            <button
                                onClick={() => { const c = { id: itemsView._clinicId || `agent_${itemsView._clinic}`, name: itemsView._clinic }; setItemsView(null); setPriceClinic(c); }}
                                title="단가표 관리 창을 엽니다 — 저장하면 매출이 다시 계산됩니다"
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '9px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer', border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca' }}>
                                단가표 수정
                            </button>
                        </div>
                    )}
                    onClose={() => setItemsView(null)}
                />
            )}
        </div>
    );
}

const inputS = { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13.5 };
const thS = { padding: '10px 14px', textAlign: 'left', fontWeight: 700, fontSize: 12.5 };
const tdS = { padding: '10px 14px' };

export default RevenueQuickView;
